import { useEffect, useRef } from 'react'

const VIDEO_ID = 'mY1CWAo8GOo'

// First interaction of any kind starts the sound. Scroll/wheel/touch cover
// "user scrolls"; pointer/click/key cover gestures browsers accept on desktop.
const TRIGGERS = ['scroll', 'wheel', 'touchstart', 'pointerdown', 'mousedown', 'keydown', 'click']

export default function BackgroundMusic() {
  const playerRef    = useRef(null)
  const divRef       = useRef(null)
  const readyRef     = useRef(false)
  const wantSoundRef = useRef(false)

  useEffect(() => {
    let cancelled = false

    function enableSound() {
      const p = playerRef.current
      if (!p) return
      try { p.unMute(); p.setVolume(70); p.playVideo() } catch {}
    }

    function onFirstInteract() {
      wantSoundRef.current = true
      removeTriggers()
      if (readyRef.current) enableSound()
    }
    function removeTriggers() {
      TRIGGERS.forEach((ev) => window.removeEventListener(ev, onFirstInteract))
    }
    TRIGGERS.forEach((ev) =>
      window.addEventListener(ev, onFirstInteract, { passive: true })
    )

    function initPlayer() {
      if (cancelled || !divRef.current) return
      playerRef.current = new window.YT.Player(divRef.current, {
        width: 200,
        height: 120,
        videoId: VIDEO_ID,
        playerVars: { autoplay: 1, mute: 1, controls: 0, loop: 1, playlist: VIDEO_ID, rel: 0, playsinline: 1 },
        events: {
          onReady: (e) => {
            readyRef.current = true
            e.target.mute()
            e.target.playVideo()
            if (wantSoundRef.current) enableSound()
          },
          onStateChange: (e) => {
            // Restart if ended (0) or unexpectedly paused (2) — keeps looping reliably
            if (e.data === 0 || e.data === 2) {
              try { e.target.playVideo() } catch {}
            }
          },
        },
      })
    }

    if (window.YT?.Player) {
      initPlayer()
    } else {
      const prev = window.onYouTubeIframeAPIReady
      window.onYouTubeIframeAPIReady = () => { if (prev) prev(); initPlayer() }
      if (!document.getElementById('yt-iframe-api')) {
        const s = document.createElement('script')
        s.id  = 'yt-iframe-api'
        s.src = 'https://www.youtube.com/iframe_api'
        document.head.appendChild(s)
      }
    }

    return () => {
      cancelled = true
      removeTriggers()
      try { playerRef.current?.destroy() } catch {}
      playerRef.current = null
      readyRef.current  = false
    }
  }, [])

  // Player must stay INSIDE the viewport or Chrome throttles playback.
  // So we keep it on-screen (bottom-right) but fully invisible & non-interactive.
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        right: 0,
        width: 200,
        height: 120,
        opacity: 0.001,  /* 0 causes Chrome to throttle cross-origin iframes as "hidden" */
        zIndex: 1,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
      aria-hidden="true"
    >
      <div ref={divRef} />
    </div>
  )
}
